import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useLocaleKeys } from '~/i18n';
import { decimalFromStr } from '~/infra/utils';
import { serviceBillingModel, serviceModel, serviceCostModel } from '~/models';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import pluralize from '~/utils/pluralize';
import { PreapprovalSettingsRow } from '../../PreapprovalFields/PreapprovalSettingsRow';
import { getPreapprovedSuffix } from '../../ServiceBuilder/ServiceBuilder.utils';
import styles from '../ServiceAdvancedSettings.module.scss';
import { AdvancedSetting } from '../ServiceAdvancedSettings.types';
import { isSettingActive } from '../ServiceAdvancedSettings.utils';
export function PreapprovedControl(props) {
    const [unitCap, setUnitCap] = useState(null);
    const { service, testId, disabled, isReadOnly, onPreapprovedAmountChange, onServiceChange, relationshipState, isFreshlyAdded } = props;
    const { t: preapprovalTexts } = useLocaleKeys('billing.preapproval');
    const [isEnabled, setIsEnabled] = useState(isSettingActive({
        type: AdvancedSetting.PreapprovedAmount,
        service
    }));
    const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing) || 'onetime';
    const isOnDemand = serviceBillingModel.isOnDemand(service.billing);
    const errors = serviceModel.validatePreapprovedAmount(service).length > 0;
    const unitSuffix = serviceCostModel.unitName(service.cost) ? pluralize(serviceCostModel.unitName(service.cost)) : preapprovalTexts.unitFallback.hours();
    const periodSuffix = getPreapprovedSuffix(recurrencePeriod);
    const suffix = periodSuffix || unitSuffix;
    const preapprovalPeriod = serviceBillingModel.preapprovalPeriod(service.billing);
    const showPeriodDropdown = isOnDemand && !!preapprovalPeriod && preapprovalPeriod !== PreapprovalPeriodType.None;
    const isPeriodDisabled = isReadOnly || relationshipState.type === 'approved' && !isFreshlyAdded;
    useEffect(()=>{
        var _serviceModel_changes, _serviceModel_changes1;
        const preapproved = serviceCostModel.unitCap(((_serviceModel_changes = serviceModel.changes(service)) === null || _serviceModel_changes === void 0 ? void 0 : _serviceModel_changes.cost) || service.cost);
        const controlledByAdvancedSettings = serviceCostModel.preapprovedControlledByAdvancedSettings(((_serviceModel_changes1 = serviceModel.changes(service)) === null || _serviceModel_changes1 === void 0 ? void 0 : _serviceModel_changes1.cost) || service.cost);
        setUnitCap(preapproved);
        if (preapproved && preapproved.gt(0)) {
            setIsEnabled(true);
        } else if (!controlledByAdvancedSettings) {
            setIsEnabled(false);
            setUnitCap(null);
        }
    }, [
        service
    ]);
    const handleChange = (checked)=>{
        setIsEnabled(checked);
        if (!checked) {
            setUnitCap(null);
            onPreapprovedAmountChange && onPreapprovedAmountChange(service.id, null, false);
        } else {
            onPreapprovedAmountChange && onPreapprovedAmountChange(service.id, unitCap, true);
        }
    };
    const handleInputChange = (value)=>{
        const parsedValue = decimalFromStr(value);
        setUnitCap(parsedValue);
        if (!value || value.trim() === '') {
            onPreapprovedAmountChange && onPreapprovedAmountChange(service.id, null, true);
        } else {
            onPreapprovedAmountChange && onPreapprovedAmountChange(service.id, parsedValue, true);
        }
    };
    const handlePeriodChange = (event)=>{
        const newPeriod = event.target.value;
        const updatedService = serviceModel.setBillingTrigger(service, serviceBillingModel.makeOnDemandBilling(newPeriod, {
            maxCharges: serviceBillingModel.maxCharges(service.billing)
        }));
        onServiceChange === null || onServiceChange === void 0 ? void 0 : onServiceChange(updatedService);
    };
    if (!serviceCostModel.isVariablePrice(service.cost)) {
        return null;
    }
    return /*#__PURE__*/ _jsx(PreapprovalSettingsRow, {
        testId: testId,
        checked: isEnabled,
        onCheckedChange: handleChange,
        disabled: disabled || isReadOnly,
        amount: unitCap ? unitCap.toString() : '',
        onAmountChange: handleInputChange,
        amountError: errors,
        amountSuffix: suffix,
        showPeriodDropdown: showPeriodDropdown,
        periodValue: preapprovalPeriod,
        onPeriodChange: handlePeriodChange,
        periodDisabled: isPeriodDisabled,
        className: styles.preapprovedFieldsRow
    });
}
